import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  Wallet,
  ArrowRightLeft,
  Plus,
  Check,
  X,
  Clock,
  ArrowUpRight,
  ArrowDownLeft,
  ChevronRight,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Banknote,
  Receipt,
  HandCoins,
  Calendar,
  AlertTriangle,
} from "lucide-react";
import { CurrencyDisplay } from "./CurrencySettings";

const RestaurantCashMovement = ({ user, restaurants: propRestaurants, onNavigate }) => {
  const [balances, setBalances] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  
  // Transfer dialog
  const [transferDialog, setTransferDialog] = useState(false);
  const [transferFrom, setTransferFrom] = useState("");
  const [transferTo, setTransferTo] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [transferNotes, setTransferNotes] = useState("");
  const [allRestaurants, setAllRestaurants] = useState([]);
  
  const [setupCompleted, setSetupCompleted] = useState(false);
  const [balanceDate, setBalanceDate] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  
  const isAdmin = user?.role === "admin" || user?.role === "superuser";
  const canInitiateTransfer = ["admin", "superuser", "manager", "restaurant_manager"].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balRes, transRes, restRes, setupRes, allRestRes] = await Promise.all([
        axiosInstance.get("/restaurant-cash/balances"),
        axiosInstance.get("/restaurant-cash/transfers"),
        axiosInstance.get("/restaurants"),
        axiosInstance.get("/restaurant-cash/setup-status").catch(() => ({ data: { setup_completed: false } })),
        axiosInstance.get("/restaurants?all=true"),
      ]);
      setBalances(balRes.data);
      setTransfers(transRes.data);
      setRestaurants(restRes.data);
      setAllRestaurants(allRestRes.data);
      setSetupCompleted(setupRes.data.setup_completed || false);
      setEffectiveDate(setupRes.data.effective_date || "");
    } catch (e) {
      toast.error("Failed to load cash data");
    } finally {
      setLoading(false);
    }
  };

  const fetchBalancesForDate = async (date) => {
    if (!date) {
      fetchData();
      return;
    }
    if (effectiveDate && date < effectiveDate) {
      toast.error(`Cannot query before the opening balance date (${effectiveDate})`);
      setBalanceDate("");
      return;
    }
    try {
      const results = await Promise.all(
        restaurants.map(r => axiosInstance.get(`/restaurant-cash/balance/${r.id}?date=${date}`))
      );
      setBalances(results.map(r => r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load balance for date");
    }
  };

  const openLedger = async (restaurantId) => {
    try {
      const res = await axiosInstance.get(`/restaurant-cash/ledger/${restaurantId}`);
      setLedger(res.data);
      const rest = restaurants.find(r => r.id === restaurantId);
      setSelectedRestaurant(rest);
      setLedgerOpen(true);
    } catch (e) {
      toast.error("Failed to load ledger");
    }
  };

  const handleTransfer = async () => {
    if (!transferFrom || !transferTo || !transferAmount) { toast.error("Fill all fields"); return; }
    if (transferFrom === transferTo) { toast.error("Source and destination must be different"); return; }
    try {
      await axiosInstance.post("/restaurant-cash/transfer", {
        from_restaurant_id: transferFrom,
        to_restaurant_id: transferTo,
        amount: parseFloat(transferAmount),
        notes: transferNotes,
      });
      toast.success("Transfer initiated — awaiting confirmation from receiving restaurant");
      setTransferDialog(false);
      setTransferAmount("");
      setTransferNotes("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to initiate transfer");
    }
  };

  const handleConfirm = async (transferId) => {
    try {
      await axiosInstance.put(`/restaurant-cash/transfer/${transferId}/confirm`);
      toast.success("Transfer confirmed");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to confirm");
    }
  };

  const handleReject = async (transferId) => {
    try {
      await axiosInstance.put(`/restaurant-cash/transfer/${transferId}/reject`);
      toast.success("Transfer rejected");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject");
    }
  };

  const formatCurrency = (val) => `₹${Number(val || 0).toLocaleString('en-IN')}`;

  const pendingForMe = transfers.filter(t => {
    if (t.status !== "pending") return false;
    if (isAdmin) return true;
    const myRestIds = user?.assigned_restaurant_ids || [];
    return myRestIds.includes(t.to_restaurant_id);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="restaurant-cash-movement">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Restaurant Cash</h2>
          <p className="text-muted-foreground">Track cash movement across restaurants</p>
        </div>
        <div className="flex gap-2">
          {canInitiateTransfer && (
            <Button size="sm" className="gap-2" onClick={() => setTransferDialog(true)} data-testid="initiate-transfer-btn">
              <ArrowRightLeft className="w-4 h-4" /> Transfer Cash
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* Pending Transfers Alert */}
      {pendingForMe.length > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="pending-transfers-alert">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <Clock className="w-5 h-5" />
              {pendingForMe.length} Pending Transfer{pendingForMe.length > 1 ? 's' : ''} — Action Required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingForMe.map(t => (
              <div key={t.id} className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border">
                <div className="flex items-center gap-3">
                  <div className="text-sm">
                    <span className="font-semibold">{t.from_restaurant_name}</span>
                    <ArrowRightLeft className="w-3 h-3 inline mx-2 text-gray-400" />
                    <span className="font-semibold">{t.to_restaurant_name}</span>
                  </div>
                  <Badge variant="outline" className="text-amber-700 border-amber-300">{formatCurrency(t.amount)}</Badge>
                  {t.notes && <span className="text-xs text-gray-500">— {t.notes}</span>}
                </div>
                <div className="flex gap-2">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="default" className="gap-1 h-7 bg-green-600 hover:bg-green-700" data-testid={`confirm-transfer-${t.id}`}>
                        <Check className="w-3 h-3" /> Confirm
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Cash Transfer?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Confirm receiving {formatCurrency(t.amount)} from {t.from_restaurant_name}. This will update both restaurants' cash balances.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleConfirm(t.id)} className="bg-green-600 hover:bg-green-700">Confirm</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="gap-1 h-7 text-red-600 hover:bg-red-50" data-testid={`reject-transfer-${t.id}`}>
                        <X className="w-3 h-3" /> Reject
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reject Cash Transfer?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Reject the {formatCurrency(t.amount)} transfer from {t.from_restaurant_name}? The transfer will be cancelled.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleReject(t.id)} className="bg-red-600 hover:bg-red-700">Reject</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Setup Required Banner */}
      {!setupCompleted && isAdmin && (
        <Card className="border-amber-300 bg-amber-50/80" data-testid="setup-required-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="font-semibold text-amber-800 text-sm">Opening Balance Setup Required</p>
                <p className="text-xs text-amber-700">
                  The balances below are based on <span className="font-semibold">all historical</span> cash revenue and expenses — not from a verified starting point. 
                  These numbers will change once you complete the Opening Balance Setup, which sets a verified starting date and distributes your actual cash across restaurants.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 h-7 text-xs border-amber-400 text-amber-800 hover:bg-amber-100 gap-1.5"
                  onClick={() => onNavigate?.("cashreconciliation")}
                  data-testid="setup-banner-btn"
                >
                  <Plus className="w-3.5 h-3.5" /> Complete Setup in Cash Reconciliation
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Balance Date Picker & Cards */}
      <div className="space-y-3">
        {setupCompleted && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <Label className="text-sm text-gray-600 whitespace-nowrap">Cash balance as of:</Label>
            <Input
              type="date"
              className="w-44 h-8 text-sm"
              value={balanceDate}
              min={effectiveDate || undefined}
              onChange={(e) => {
                setBalanceDate(e.target.value);
                fetchBalancesForDate(e.target.value);
              }}
              data-testid="balance-date-picker"
            />
          </div>
          {balanceDate && (
            <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={() => { setBalanceDate(""); fetchData(); }} data-testid="clear-date-filter">
              Clear
            </Button>
          )}
          {balanceDate && <span className="text-xs text-blue-600">Showing balances as of {balanceDate}</span>}
        </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="balance-cards">
        {balances.map(b => {
          const bal = setupCompleted ? b : { ...b, current_balance: 0, opening_balance: 0, cash_revenue: 0, cash_expenses: 0, incoming_transfers: 0, outgoing_transfers: 0, cash_handovers: 0 };
          return (
          <Card key={b.restaurant_id} className={`cursor-pointer hover:shadow-md transition-shadow ${!setupCompleted ? 'border-dashed border-amber-200' : ''}`} onClick={() => setupCompleted ? openLedger(b.restaurant_id) : null} data-testid={`cash-card-${b.restaurant_id}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm">{b.restaurant_name}</h3>
                <div className="flex items-center gap-1">
                  {!setupCompleted && <Badge variant="outline" className="text-[10px] h-5 border-amber-300 text-amber-600">Setup Pending</Badge>}
                  {setupCompleted && <ChevronRight className="w-4 h-4 text-gray-400" />}
                </div>
              </div>
              <div className="text-2xl font-bold mb-3">
                <CurrencyDisplay amount={bal.current_balance} />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1.5 text-gray-500">
                  <Banknote className="w-3.5 h-3.5 text-blue-500" />
                  <span>Opening: {formatCurrency(bal.opening_balance)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-500">
                  <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                  <span>Revenue: {formatCurrency(bal.cash_revenue)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-500">
                  <Receipt className="w-3.5 h-3.5 text-red-500" />
                  <span>Expenses: {formatCurrency(bal.cash_expenses)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-500">
                  <ArrowRightLeft className="w-3.5 h-3.5 text-purple-500" />
                  <span>Net Transfers: {formatCurrency(bal.incoming_transfers - bal.outgoing_transfers)}</span>
                </div>
                {setupCompleted && (bal.cash_handovers || 0) > 0 && (
                  <div className="flex items-center gap-1.5 text-gray-500 col-span-2">
                    <HandCoins className="w-3.5 h-3.5 text-amber-500" />
                    <span>Handovers: {formatCurrency(bal.cash_handovers)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          );
        })}
        </div>
      </div>

      {/* Recent Transfers */}
      <Card data-testid="recent-transfers">
        <CardHeader>
          <CardTitle className="text-base">Recent Cash Transfers</CardTitle>
        </CardHeader>
        <CardContent>
          {transfers.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No transfers yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Initiated By</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transfers.slice(0, 20).map(t => (
                  <TableRow key={t.id}>
                    <TableCell className="text-sm">{new Date(t.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                    <TableCell className="text-sm font-medium">{t.from_restaurant_name}</TableCell>
                    <TableCell className="text-sm font-medium">{t.to_restaurant_name}</TableCell>
                    <TableCell className="text-sm text-right font-semibold">{formatCurrency(t.amount)}</TableCell>
                    <TableCell>
                      <Badge variant={t.status === 'confirmed' ? 'default' : t.status === 'rejected' ? 'destructive' : 'secondary'}
                        className={t.status === 'confirmed' ? 'bg-green-100 text-green-800 hover:bg-green-100' : t.status === 'pending' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' : ''}>
                        {t.status === 'confirmed' && <Check className="w-3 h-3 mr-1" />}
                        {t.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                        {t.status === 'rejected' && <X className="w-3 h-3 mr-1" />}
                        {t.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">{t.initiated_by_name}</TableCell>
                    <TableCell className="text-sm text-gray-400 max-w-[150px] truncate">{t.notes || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Ledger Dialog */}
      <Dialog open={ledgerOpen} onOpenChange={setLedgerOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="cash-ledger-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg">{selectedRestaurant?.name} — Cash Ledger</DialogTitle>
          </DialogHeader>

          {/* Balance Breakdown Summary */}
          {ledger?.summary && (
            <div className="rounded-lg border bg-gray-50 p-4 space-y-2" data-testid="ledger-summary">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-gray-600"><Banknote className="w-4 h-4 text-blue-500" /> Opening Balance</span>
                <span className="font-semibold">{formatCurrency(ledger.summary.opening_balance)}</span>
              </div>
              {ledger.summary.total_revenue > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-gray-600"><TrendingUp className="w-4 h-4 text-green-500" /> Cash Revenue</span>
                  <span className="font-semibold text-green-700">+ {formatCurrency(ledger.summary.total_revenue)}</span>
                </div>
              )}
              {ledger.summary.total_expenses > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-gray-600"><TrendingDown className="w-4 h-4 text-red-500" /> Cash Expenses</span>
                  <span className="font-semibold text-red-700">− {formatCurrency(ledger.summary.total_expenses)}</span>
                </div>
              )}
              {ledger.summary.total_transfers_in > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-gray-600"><ArrowDownLeft className="w-4 h-4 text-green-500" /> Transfers In</span>
                  <span className="font-semibold text-green-700">+ {formatCurrency(ledger.summary.total_transfers_in)}</span>
                </div>
              )}
              {ledger.summary.total_transfers_out > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-gray-600"><ArrowUpRight className="w-4 h-4 text-red-500" /> Transfers Out</span>
                  <span className="font-semibold text-red-700">− {formatCurrency(ledger.summary.total_transfers_out)}</span>
                </div>
              )}
              {ledger.summary.total_handovers > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-gray-600"><HandCoins className="w-4 h-4 text-amber-500" /> Handovers</span>
                  <span className="font-semibold text-red-700">− {formatCurrency(ledger.summary.total_handovers)}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-1 flex items-center justify-between">
                <span className="font-bold text-sm">Current Balance</span>
                <span className={`text-lg font-bold ${ledger.summary.current_balance >= 0 ? 'text-green-700' : 'text-red-700'}`} data-testid="ledger-current-balance">
                  {formatCurrency(ledger.summary.current_balance)}
                </span>
              </div>
            </div>
          )}

          {/* Ledger Entries Table */}
          <div className="flex-1 overflow-y-auto">
            {ledger?.entries?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right text-green-700">Credit</TableHead>
                    <TableHead className="text-right text-red-700">Debit</TableHead>
                    <TableHead className="text-right font-bold">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledger.entries.map((e, i) => (
                    <TableRow key={i} className={e.type === 'opening' ? 'bg-blue-50/70' : ''} data-testid={`ledger-entry-${i}`}>
                      <TableCell className="text-sm whitespace-nowrap">{e.date}</TableCell>
                      <TableCell className="text-sm">
                        {e.type === 'opening' && <Banknote className="w-3.5 h-3.5 inline mr-1 text-blue-600" />}
                        {e.type === 'revenue' && <TrendingUp className="w-3.5 h-3.5 inline mr-1 text-green-600" />}
                        {e.type === 'expense' && <TrendingDown className="w-3.5 h-3.5 inline mr-1 text-red-600" />}
                        {e.type === 'transfer_in' && <ArrowDownLeft className="w-3.5 h-3.5 inline mr-1 text-green-600" />}
                        {e.type === 'transfer_out' && <ArrowUpRight className="w-3.5 h-3.5 inline mr-1 text-red-600" />}
                        {e.type === 'handover' && <HandCoins className="w-3.5 h-3.5 inline mr-1 text-amber-600" />}
                        {e.description}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">{e.source}</TableCell>
                      <TableCell className="text-sm text-right text-green-700">{e.credit > 0 ? formatCurrency(e.credit) : ''}</TableCell>
                      <TableCell className="text-sm text-right text-red-700">{e.debit > 0 ? formatCurrency(e.debit) : ''}</TableCell>
                      <TableCell className="text-sm text-right font-semibold">{formatCurrency(e.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-gray-500 text-center py-10">No ledger entries. Set an opening balance to begin.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Initiate Cash Transfer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>From Restaurant</Label>
              <Select value={transferFrom} onValueChange={setTransferFrom}>
                <SelectTrigger data-testid="transfer-from"><SelectValue placeholder="Source..." /></SelectTrigger>
                <SelectContent>
                  {restaurants.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {transferFrom && (() => {
              const bal = balances.find(b => b.restaurant_id === transferFrom);
              const currentBal = bal?.current_balance || 0;
              const amt = parseFloat(transferAmount) || 0;
              const afterBal = currentBal - amt;
              const isInsufficient = amt > 0 && amt > currentBal;
              return (
                <div className={`rounded-lg p-3 text-sm border ${isInsufficient ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`} data-testid="transfer-balance-preview">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Balance:</span>
                    <span className="font-semibold">{formatCurrency(currentBal)}</span>
                  </div>
                  {amt > 0 && (
                    <>
                      <div className="flex justify-between text-red-600">
                        <span>Transfer Amount:</span>
                        <span>- {formatCurrency(amt)}</span>
                      </div>
                      <div className={`flex justify-between font-bold border-t pt-1 mt-1 ${afterBal < 0 ? 'text-red-700' : 'text-green-700'}`}>
                        <span>Balance After:</span>
                        <span>{formatCurrency(afterBal)}</span>
                      </div>
                      {isInsufficient && (
                        <p className="text-xs text-red-600 mt-1">⚠ Insufficient balance for this transfer</p>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
            <div>
              <Label>To Restaurant</Label>
              <Select value={transferTo} onValueChange={setTransferTo}>
                <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Destination..." /></SelectTrigger>
                <SelectContent>
                  {allRestaurants.filter(r => r.id !== transferFrom).map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {transferTo && (() => {
              const bal = balances.find(b => b.restaurant_id === transferTo);
              const currentBal = bal?.current_balance || 0;
              const amt = parseFloat(transferAmount) || 0;
              return amt > 0 ? (
                <div className="rounded-lg p-3 text-sm border bg-green-50 border-green-200" data-testid="transfer-dest-preview">
                  <div className="flex justify-between text-gray-600">
                    <span>Current Balance ({bal?.restaurant_name}):</span>
                    <span className="font-semibold">{formatCurrency(currentBal)}</span>
                  </div>
                  <div className="flex justify-between text-green-700 font-bold border-t pt-1 mt-1">
                    <span>Balance After:</span>
                    <span>{formatCurrency(currentBal + amt)}</span>
                  </div>
                </div>
              ) : null;
            })()}
            <div>
              <Label>Amount (Rs.)</Label>
              <Input type="number" value={transferAmount} onChange={e => setTransferAmount(e.target.value)} placeholder="e.g. 10000" data-testid="transfer-amount" />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input value={transferNotes} onChange={e => setTransferNotes(e.target.value)} placeholder="Reason for transfer" data-testid="transfer-notes" />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              The receiving restaurant will need to <strong>confirm</strong> this transfer before it affects balances.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialog(false)}>Cancel</Button>
            <Button onClick={handleTransfer} disabled={!transferFrom || !transferTo || !transferAmount || (parseFloat(transferAmount) > (balances.find(b => b.restaurant_id === transferFrom)?.current_balance || 0))} data-testid="submit-transfer-btn">Initiate Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RestaurantCashMovement;
